import React, { useEffect, useState } from "react";
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_KEYS,
  FeatureFlagKey,
  FeatureFlagSource,
  useResolvedFlag,
} from "../../utils/featureFlags";
import {
  BENEFICIOS_APLICAR_OFV,
  parseBenefitIds,
  useResolvedBeneficiosAplicar,
} from "../../utils/beneficiosAplicar";

import { useDocTitle } from "../../utils/hooks";
import { useGlobalStore } from "../../utils/global";

const SESSION_UNLOCK_KEY = "devtools:unlocked";
const ENV_KEY = process.env.REACT_APP_DEVTOOLS_KEY;

const SOURCE_LABELS: Record<FeatureFlagSource, string> = {
  override: "Override local",
  server: "Servidor",
  env: "Env",
  default: "Default",
};

function SourceBadge({ source }: { source: FeatureFlagSource }) {
  const styles: Record<FeatureFlagSource, string> = {
    override: "bg-primary-100 text-neutral-white",
    server: "bg-semantic-info text-neutral-white",
    env: "bg-gray-10 text-gray-70",
    default: "bg-gray-10 text-gray-70",
  };
  return (
    <span
      className={`inline-block rounded px-2 py-1 text-caption font-semibold ${styles[source]}`}
    >
      {SOURCE_LABELS[source]}
    </span>
  );
}

type SegmentValue = "on" | "off" | "default";

function SegmentedControl({
  value,
  onChange,
}: {
  value: SegmentValue;
  onChange: (value: SegmentValue) => void;
}) {
  const segments: { id: SegmentValue; label: string }[] = [
    { id: "on", label: "On" },
    { id: "off", label: "Off" },
    { id: "default", label: "Default" },
  ];
  return (
    <div className="inline-flex gap-2">
      {segments.map((seg) => (
        <button
          key={seg.id}
          type="button"
          aria-pressed={value === seg.id}
          onClick={() => onChange(seg.id)}
          className={`btn btn-small ${
            value === seg.id ? "btn-primary" : "btn-secondary"
          }`}
        >
          {seg.label}
        </button>
      ))}
    </div>
  );
}

function FlagRow({ flagKey }: { flagKey: FeatureFlagKey }) {
  const def = FEATURE_FLAGS[flagKey];
  const resolved = useResolvedFlag(flagKey);
  const override = useGlobalStore((s) => s.featureFlagOverrides[flagKey]);
  const setFeatureFlagOverride = useGlobalStore(
    (s) => s.setFeatureFlagOverride
  );

  const segment: SegmentValue =
    override === undefined ? "default" : override ? "on" : "off";

  const onChange = (value: SegmentValue) => {
    if (value === "default") {
      setFeatureFlagOverride(flagKey, undefined);
    } else {
      setFeatureFlagOverride(flagKey, value === "on");
    }
  };

  return (
    <div className="bg-neutral-white border border-gray-10 rounded-lg shadow-base p-4 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <p className="font-semibold text-gray-80">{def.label}</p>
            <SourceBadge source={resolved.source} />
            <span
              className={`text-body-2 font-semibold ${
                resolved.value ? "text-semantic-success" : "text-gray-50"
              }`}
            >
              {resolved.value ? "Activo" : "Inactivo"}
            </span>
          </div>
          <p className="mt-1 text-body-2 font-body text-gray-70">
            {def.description}
          </p>
          <p className="mt-1 text-caption text-gray-50">
            <code>{def.key}</code>
          </p>
        </div>
        <div className="shrink-0">
          <SegmentedControl value={segment} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}

function BeneficiosAplicarRow() {
  const resolved = useResolvedBeneficiosAplicar();
  const override = useGlobalStore((s) => s.beneficiosAplicarOverride);
  const setOverride = useGlobalStore((s) => s.setBeneficiosAplicarOverride);
  const [draft, setDraft] = useState(override ?? "");

  useEffect(() => {
    setDraft(override ?? "");
  }, [override]);

  const ids = Array.from(parseBenefitIds(resolved.value)).join(", ");

  const guardar = () => {
    const trimmed = draft.trim();
    setOverride(trimmed === "" ? undefined : trimmed);
  };

  return (
    <div className="bg-neutral-white border border-gray-10 rounded-lg shadow-base p-4 md:p-6">
      <div className="flex flex-col gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <p className="font-semibold text-gray-80">
              {BENEFICIOS_APLICAR_OFV.label}
            </p>
            <SourceBadge source={resolved.source} />
          </div>
          <p className="mt-1 text-body-2 font-body text-gray-70">
            {BENEFICIOS_APLICAR_OFV.description}
          </p>
          <p className="mt-1 text-caption text-gray-50">
            <code>{BENEFICIOS_APLICAR_OFV.serverParamCode}</code> · IDs activos:{" "}
            {ids || "ninguno"}
          </p>
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            type="text"
            className="form-control"
            placeholder="Ej. 13,5"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="btn btn-primary btn-small"
              onClick={guardar}
            >
              Guardar
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={() => setOverride(undefined)}
            >
              Default
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DevToolsPanel() {
  const setFeatureFlagOverride = useGlobalStore(
    (s) => s.setFeatureFlagOverride
  );
  const setBeneficiosAplicarOverride = useGlobalStore(
    (s) => s.setBeneficiosAplicarOverride
  );

  const resetAll = () => {
    FEATURE_FLAG_KEYS.forEach((key) =>
      setFeatureFlagOverride(key, undefined)
    );
    setBeneficiosAplicarOverride(undefined);
  };

  return (
    <div className="space-y-4">
      {FEATURE_FLAG_KEYS.map((key) => (
        <FlagRow key={key} flagKey={key} />
      ))}

      <BeneficiosAplicarRow />

      <div className="flex flex-col gap-2 pt-2 md:flex-row md:items-center md:justify-between">
        <button
          type="button"
          className="btn btn-secondary btn-medium"
          onClick={resetAll}
        >
          Restablecer overrides
        </button>
        <p className="text-caption text-gray-50">
          Los overrides aplican solo a este navegador.
        </p>
      </div>
    </div>
  );
}

function LockScreen({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (ENV_KEY && value === ENV_KEY) {
      try {
        sessionStorage.setItem(SESSION_UNLOCK_KEY, "true");
      } catch {
        /* ignore */
      }
      onUnlock();
    } else {
      setError(true);
    }
  };

  return (
    <div className="bg-neutral-white border border-gray-10 rounded-lg shadow-box p-6 md:p-8 max-w-md mx-auto">
      <h3 className="text-primary-100 font-bold text-lead-lg mb-2">
        Acceso restringido
      </h3>
      <p className="text-body-2 text-gray-70 mb-6">
        Ingresa la clave para administrar los feature flags.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div className="form-group">
          <label>Clave de acceso</label>
          <input
            type="password"
            className="form-control large"
            placeholder="Ingresa la clave"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(false);
            }}
            autoFocus
          />
        </div>
        {error && (
          <p className="text-semantic-error text-body-2">
            Clave incorrecta.
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-large w-full">
          Desbloquear
        </button>
      </form>
    </div>
  );
}

const DevTools = () => {
  useDocTitle("Developer Tools");

  const initiallyUnlocked = (() => {
    try {
      return sessionStorage.getItem(SESSION_UNLOCK_KEY) === "true";
    } catch {
      return false;
    }
  })();
  const [unlocked, setUnlocked] = useState(initiallyUnlocked);

  return (
    <div className="min-h-screen bg-gray-5 py-10 md:py-14">
      <div className="container">
        <header className="mb-8">
          <h1 className="text-primary-100 font-bold">Developer Tools</h1>
          <p className="mt-2 text-body-2 text-gray-70">
            Administración de feature flags de OFV.
          </p>
        </header>

        {!ENV_KEY ? (
          <div className="bg-neutral-white border border-gray-10 rounded-lg shadow-base p-6 max-w-md mx-auto">
            <p className="text-body-2 text-gray-70">
              Esta página está deshabilitada en este entorno.
            </p>
          </div>
        ) : unlocked ? (
          <DevToolsPanel />
        ) : (
          <LockScreen onUnlock={() => setUnlocked(true)} />
        )}
      </div>
    </div>
  );
};

export default DevTools;
